import React from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import closeIcon from "../../assets/cross.svg";

// Permanent, irreversible removal of a student (test/junk cleanup). Distinct
// from Deactivate (reversible archive) and Depart (left the school) — this
// erases the account + data and frees the email. Strong red confirm.
const DeleteStudentModal = ({ student, onClose, onConfirm, isDeleting }) => {
  const { t } = useTranslation();
  const name =
    `${student?.firstName || ""} ${student?.lastName || ""}`.trim() || student?.email || t("manageStudents.na");

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="w-[500px] bg-white rounded-[20px] overflow-hidden font-['Manrope']">
        {/* Header */}
        <div className="p-6 bg-gradient-to-br from-white to-red-50 border-b border-red-100 flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">
            {t("modals.deleteStudent.title", { defaultValue: "Delete student permanently?" })}
          </h2>
          <button onClick={onClose} className="p-1" disabled={isDeleting}>
            <img src={closeIcon} alt={t("modals.common.close", { defaultValue: "Close" })} className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-6 flex flex-col gap-8">
          <div className="text-[16px] leading-normal">
            <span className="text-gray-500 font-medium">
              {t("modals.deleteStudent.body", { defaultValue: "Permanently delete {{name}}?", name })}{" "}
            </span>
            <span className="text-gray-900 font-bold">
              {t("modals.deleteStudent.warning", {
                defaultValue:
                  "This erases their account and all their data, removes them from your school, and frees their email. It can't be undone.",
              })}
            </span>
            <br />
            <br />
            <span className="text-gray-500 font-medium">
              {t("modals.deleteStudent.hint", {
                defaultValue: "To keep their record but remove access, use Deactivate instead.",
              })}
            </span>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-gray-900 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("modals.deleteStudent.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              onClick={onConfirm}
              disabled={isDeleting}
              className="h-11 px-4 py-1.5 text-[16px] font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isDeleting && <Loader2 size={16} className="animate-spin" />}
              {isDeleting
                ? t("modals.deleteStudent.deleting", { defaultValue: "Deleting…" })
                : t("modals.deleteStudent.confirm", { defaultValue: "Delete permanently" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteStudentModal;
